import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ASSETS, VENTURES, CONTACT_EMAILS, HANDLE } from "@/lib/brand";
import Seo from "@/components/Seo";

const PORTRAITS = [
  { key: "portrait1", label: "Portrait · 01", caption: "Waterfront, NYC skyline · Black on black tailoring", url: ASSETS.portrait1, filename: "miguel-santana-portrait-01.png" },
  { key: "portrait2", label: "Portrait · 02", caption: "Empire State · Manhattan street", url: ASSETS.portrait2, filename: "miguel-santana-portrait-02.png" },
  { key: "portrait3", label: "Portrait · 03", caption: "W 34 St · Selfie · Hand-held", url: ASSETS.portrait3, filename: "miguel-santana-portrait-03.png" },
];

const MARKS = [
  { key: "monogram", label: "MS Monogram", caption: "Primary mark · Navy + silver", url: ASSETS.monogram, filename: "ms-monogram.png", aspect: "square" },
  { key: "signature", label: "Cursive Signature", caption: "Founder signature · Navy ink", url: ASSETS.signature, filename: "miguel-santana-signature.png", aspect: "wide" },
];

const BIO_SHORT = `Miguel Santana is a Dominican-American entrepreneur, media personality, and founder of MPR Ventures — a multi-city American conglomerate.`;

const BIO_MEDIUM = `Miguel Santana is a Dominican-American entrepreneur, media personality, aspiring author, and founder. He is the founder and chief executive of MPR Ventures, a privately held conglomerate that operates a portfolio of five companies spanning hospitality (Court Street Group · Las Vegas), consumer brands (Pacific Standard Brands · Los Angeles), capital and technology (Skyline Global · New York), cross-border ventures (Santana Enterprises · Miami), and civic impact (Better Tomorrow Initiative · Washington DC).`;

const BIO_LONG = `Miguel Santana is a Dominican-American entrepreneur, media personality, aspiring author, and founder. Born in Santo Domingo on October 1, 1987, he immigrated to New York City with his family before the age of two and grew up between the boroughs of New York and, later, Atlanta and Boston.

He is the founder and chief executive of MPR Ventures, a privately held conglomerate operating five companies across five American cities — Court Street Group in Las Vegas (hospitality), Pacific Standard Brands in Los Angeles (consumer brands and lifestyle), Skyline Global in New York (capital and technology), Santana Enterprises in Miami (cross-border ventures), and the Better Tomorrow Initiative in Washington DC (philanthropy and civic impact).

His broader project portfolio includes TruFlights, Fresh Start Credit, Skyline (social), Skyline TV, Standard Society, Athlete City, the television and book projects "Same World" and "Built Different," and the podcasts "Raise The Standard," "Dating Rx," "The Voice of Reason," and "Miguel Talks Sports."

Through writing, podcasts, and his operating companies, Santana explores the intersection of business, identity, culture, sports, and modern society — with a long-term mission of building ideas, platforms, and opportunities that encourage people to think bigger, pursue their goals, and recognize that setbacks do not define their future.`;

const FACT_SHEET = [
  ["Name", "Miguel Santana"],
  ["Born", "October 1, 1987 · Santo Domingo, Dominican Republic"],
  ["Heritage", "Dominican-American"],
  ["Title", "Founder & Chief Executive, MPR Ventures"],
  ["Based", "New York"],
  ["Disciplines", "Business · Media · Sports · Culture · Philanthropy"],
  ["Brand Pillars", "The Educator · The Entrepreneur · The Entertainer — \"The ThrEEE's to Success\""],
  ["Operating Companies", "5 · Court Street Group, Pacific Standard Brands, Skyline Global, Santana Enterprises, Better Tomorrow Initiative"],
  ["Cities", "Las Vegas · Los Angeles · New York · Miami · Washington DC"],
  ["Press Contact", "media@iammiguelsantana.com"],
  ["Booking", "podcast@iammiguelsantana.com"],
  ["Handle", HANDLE],
];

function copyToClipboard(text, onDone) {
  navigator.clipboard.writeText(text).then(() => {
    if (onDone) onDone();
  }).catch(() => {});
}

function DownloadCard({ item, type = "portrait" }) {
  return (
    <div data-testid={`press-${type}-${item.key}`} className="border hairline overflow-hidden bg-navy-surface group">
      <div className={`relative overflow-hidden ${item.aspect === "wide" ? "aspect-[16/9] bg-navy-elevated" : item.aspect === "square" ? "aspect-square bg-navy-elevated" : "aspect-[3/4]"}`}>
        <img
          src={item.url}
          alt={item.label}
          className={`w-full h-full ${item.key === "signature" ? "object-contain p-12 bg-white/[0.04]" : item.key === "monogram" ? "object-contain p-8" : "object-cover"}`}
        />
        <div className="absolute inset-0 grain pointer-events-none" />
      </div>
      <div className="p-6 flex items-end justify-between gap-4">
        <div>
          <p className="font-overline text-champagne">{item.label}</p>
          <p className="mt-2 font-body text-silver-muted text-sm leading-relaxed">{item.caption}</p>
        </div>
        <a
          href={item.url}
          download={item.filename}
          target="_blank"
          rel="noreferrer"
          data-testid={`press-download-${item.key}`}
          className="btn-editorial whitespace-nowrap"
        >
          Download ↓
        </a>
      </div>
    </div>
  );
}

function BioBlock({ label, text, testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(text, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    });
  };
  return (
    <div data-testid={testId} className="border hairline p-8 md:p-10 bg-navy-surface">
      <div className="flex items-center justify-between mb-4 gap-4">
        <p className="font-overline text-champagne">{label}</p>
        <button
          onClick={handleCopy}
          data-testid={`${testId}-copy`}
          className="font-overline text-silver-muted hover:text-silver transition-colors"
        >
          {copied ? "Copied ✓" : "Copy →"}
        </button>
      </div>
      <p className="font-body text-silver text-base md:text-lg leading-[1.85] whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export default function Press() {
  return (
    <div data-testid="press-page" className="pt-32 pb-20">
      <Seo
        title="Press Kit · Downloads"
        description="Approved portraits, brand marks, biographical copy, and fact sheet for journalists, podcast producers, and brand partners covering Miguel Santana and MPR Ventures."
        path="/press"
      />
      {/* Hero */}
      <section className="px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-6">— Press Kit</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7.5rem] text-silver leading-[0.9] tracking-tighter max-w-6xl">
            Press kit<br />
            <span className="italic font-light">& downloads.</span>
          </h1>
          <p className="mt-12 max-w-3xl font-body text-silver-muted text-lg leading-relaxed">
            Approved imagery, brand marks, and biographical copy for journalists, podcast
            producers, booking agents, and brand partners. All assets are property of MPR
            Ventures, LLC and may be used for editorial coverage with proper attribution.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#portraits" data-testid="press-nav-portraits" className="btn-editorial">Portraits ↓</a>
            <a href="#marks" data-testid="press-nav-marks" className="btn-editorial">Brand Marks ↓</a>
            <a href="#bio" data-testid="press-nav-bio" className="btn-editorial">Biography ↓</a>
            <a href="#fact-sheet" data-testid="press-nav-fact-sheet" className="btn-editorial">Fact Sheet ↓</a>
            <a href="#contact" data-testid="press-nav-contact" className="btn-editorial btn-editorial-filled">Press Contact ↓</a>
          </div>
        </div>
      </section>

      {/* Portraits */}
      <section id="portraits" className="mt-24 md:mt-32 px-6 md:px-12 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-3">— 01</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05]">Approved Portraits</h2>
            </div>
            <p className="font-body text-silver-muted max-w-sm text-sm">
              Right-click "Download" to save the full-resolution image. Credit: Office of Miguel Santana.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="press-portrait-grid">
            {PORTRAITS.map((p) => <DownloadCard key={p.key} item={p} type="portrait" />)}
          </div>
        </div>
      </section>

      {/* Brand Marks */}
      <section id="marks" className="mt-24 md:mt-32 px-6 md:px-12 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-3">— 02</p>
          <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05] mb-12">Brand Marks</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="press-marks-grid">
            {MARKS.map((m) => <DownloadCard key={m.key} item={m} type="mark" />)}
          </div>

          <div className="mt-10 border hairline p-6 md:p-8">
            <p className="font-overline text-champagne mb-3">Usage Notes</p>
            <ul className="font-body text-silver-muted text-sm space-y-2 leading-relaxed list-disc pl-5">
              <li>Do not distort, recolor, or apply effects to the monogram or signature.</li>
              <li>Maintain clear space of at least the height of the MS monogram around the mark.</li>
              <li>Use on contrasting backgrounds only — navy or off-white preferred.</li>
              <li>For custom format requests (SVG, EPS, vector lockups), email media@iammiguelsantana.com.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Biography blocks */}
      <section id="bio" className="mt-24 md:mt-32 px-6 md:px-12 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-3">— 03</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05]">Biography — Pull Copy</h2>
            </div>
            <Link to="/about" className="btn-editorial">Full Biography →</Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BioBlock label="Short · One line" text={BIO_SHORT} testId="bio-short" />
            <BioBlock label="Medium · Press" text={BIO_MEDIUM} testId="bio-medium" />
            <div className="lg:col-span-2">
              <BioBlock label="Long · Editorial" text={BIO_LONG} testId="bio-long" />
            </div>
          </div>
        </div>
      </section>

      {/* Fact Sheet */}
      <section id="fact-sheet" className="mt-24 md:mt-32 px-6 md:px-12 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-3">— 04</p>
          <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05] mb-12">Fact Sheet</h2>

          <div className="border-y hairline" data-testid="press-fact-sheet">
            {FACT_SHEET.map(([k, v]) => (
              <div key={k} className="grid grid-cols-12 gap-4 py-5 border-b hairline last:border-b-0">
                <span className="col-span-12 md:col-span-3 font-overline text-silver-muted">{k}</span>
                <span className="col-span-12 md:col-span-9 font-body text-silver">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ventures quick-reference */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-3">— 05</p>
          <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05] mb-12">The Conglomerate — At a Glance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border hairline">
            {VENTURES.map((v, i) => (
              <div key={v.id} className="bg-navy p-6">
                <p className="font-overline text-silver-muted">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 font-display text-xl text-silver leading-tight">{v.name}</h3>
                <p className="mt-3 font-overline text-champagne">{v.city}</p>
                <p className="mt-3 font-body text-silver-muted text-xs leading-relaxed">{v.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Contact */}
      <section id="contact" className="mt-24 md:mt-32 px-6 md:px-12 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto border hairline-strong p-10 md:p-16 bg-navy-surface grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-7">
            <p className="font-overline text-champagne mb-4">— 06 · Press Contact</p>
            <h2 className="font-display text-3xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
              Reach the Office<br />
              <span className="italic font-light">directly.</span>
            </h2>
            <p className="mt-6 font-body text-silver-muted max-w-2xl">
              For interview requests, podcast bookings, features, and custom asset requests
              (SVG/vector marks, additional portraits, b-roll), please email the appropriate
              direct line below. Standard reply cycle is 3 business days.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-10 md:border-l hairline">
            <ul className="space-y-4">
              {CONTACT_EMAILS.map((c) => (
                <li key={c.value} className="border-b hairline pb-3">
                  <p className="font-overline text-silver-muted">{c.label}</p>
                  <a
                    href={`mailto:${c.value}`}
                    data-testid={`press-email-${c.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                    className="font-display text-lg text-silver hover:text-champagne transition-colors block mt-1"
                  >
                    {c.value}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="press-cta-form" className="btn-editorial btn-editorial-filled">
                Open Booking Form
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
